import { useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { App } from 'antd'
import { DownloadOutlined, PaperClipOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { api, day, download, money } from '../api'
import { isStaff, useSession } from '../session'
import { ClaimTable } from './Claims'

/**
 * One policy, for its holder, its beneficiary or the insurer's staff. A bond
 * shows both parties, where its money is held and when it comes back; either
 * party may attach evidence — the flat's condition at move-in — and the staff
 * may record that a lease ended early.
 */
export default function PolicyDetail() {
  const { id = '' } = useParams()
  const { t } = useTranslation()
  const { message } = App.useApp()
  const qc = useQueryClient()
  const { me, account } = useSession()
  const policy = useQuery({ queryKey: ['policy', id], queryFn: () => api.policy(id), refetchInterval: 15_000 })
  const [file, setFile] = useState<File | null>(null)
  const [note, setNote] = useState('')
  const [ending, setEnding] = useState(false)
  const input = useRef<HTMLInputElement>(null)

  const refresh = () => {
    for (const k of [['policy', id], ['policies'], ['me']]) qc.invalidateQueries({ queryKey: k })
  }
  const attach = useMutation({
    mutationFn: () => {
      const form = new FormData()
      form.set('evidence', file!)
      form.set('note', note)
      return api.attach(id, form)
    },
    onSuccess: () => { setFile(null); setNote(''); message.success(t('saved')); refresh() },
    onError: (e: Error) => message.error(e.message),
  })
  const end = useMutation({
    mutationFn: () => api.endLease(id),
    onSuccess: () => { setEnding(false); message.success(t('done')); refresh() },
    onError: (e: Error) => message.error(e.message),
  })

  const p = policy.data?.data
  if (!p) return <div className="page page-mid"><div className="skeleton" style={{ height: 120 }} /></div>
  const bond = p.kind === 'bond'
  const party = p.holder === account || p.beneficiary === account
  const staff = isStaff(me) || me?.roles.includes('operator')

  return (
    <div className="page page-mid stack">
      <div className="panel">
        <div className="panel-head">
          <span className="chip">{bond ? t('bondWord') : t('coverWord')}</span>
          <span className="grow">{p.product_name || p.product}</span>
          <span className={`state ${p.status}`}>{t(p.status)}</span>
        </div>
        <div className="panel-body">
          <dl className="review-grid">
            <dt>{t('policyId')}</dt><dd className="mono">{p.id}</dd>
            <dt>{bond ? t('renter') : t('holder')}</dt><dd>{p.holder}</dd>
            {bond && <><dt>{t('landlord')}</dt><dd>{p.beneficiary}</dd></>}
            <dt>{bond ? t('bondAmount') : t('sumInsured')}</dt><dd className="num">{money(p.coverage)}</dd>
            <dt>{bond ? t('inEscrow') : t('coverLeft')}</dt><dd className="num">{money(p.status === 'released' ? 0 : p.remaining)}</dd>
            <dt>{bond ? t('fee') : t('premiumWord')}</dt><dd className="num">{money(p.premium)}</dd>
            <dt>{t('starts')}</dt><dd>{day(p.starts_at)}</dd>
            <dt>{p.status === 'cancelled' ? t('cancelled') : t('ends')}</dt><dd>{day(p.status === 'cancelled' ? p.cancelled_at : p.ends_at)}</dd>
            {bond && <><dt>{t('releaseOn')}</dt><dd>{day(p.release_at)}</dd></>}
            {p.status === 'released' && <><dt>{t('releasedWord')}</dt><dd className="num">{money(p.released)}</dd></>}
            {bond && staff && <><dt>{t('escrowAccount')}</dt><dd>{p.escrow}</dd></>}
            {p.referrer && <><dt>{t('referredBy')}</dt><dd>{p.referrer}</dd></>}
          </dl>
          {bond && staff && p.status === 'active' && (
            ending ? (
              <div className="stack" style={{ gap: 8, marginTop: 12 }}>
                <p className="notice">{t('endLeaseConfirm')}</p>
                <div className="row">
                  <button className="btn-ghost" onClick={() => setEnding(false)}>{t('cancel')}</button>
                  <button className="btn-small" disabled={end.isPending} onClick={() => end.mutate()}>{t('endLease')}</button>
                </div>
              </div>
            ) : (
              <div className="row" style={{ marginTop: 12 }}>
                <span className="topbar-spacer" />
                <button className="btn-ghost" onClick={() => setEnding(true)}>{t('endLease')}</button>
              </div>
            )
          )}
        </div>
      </div>

      {p.external && (
        <div className="panel" id="external">
          <div className="panel-head">
            <span className="grow">{t('externalContract')}</span>
            <span className={`state ${p.external.status === 'registered' ? 'active' : 'cancelled'}`}>
              {t(p.external.status === 'pending' ? 'pendingWord' : p.external.status, { defaultValue: p.external.status })}
            </span>
          </div>
          <div className="panel-body">
            <dl className="review-grid">
              <dt>{t('externalSource')}</dt><dd>{p.external.source.toUpperCase()}</dd>
              {p.external.number && <><dt>{t('externalNumber')}</dt><dd className="mono">{p.external.number}</dd></>}
              <dt>{t('externalPremium')}</dt><dd className="num">{money(p.external.premium)}</dd>
              {p.external.starts_at && <><dt>{t('starts')}</dt><dd>{day(p.external.starts_at)}</dd></>}
              {p.external.ends_at && <><dt>{t('ends')}</dt><dd>{day(p.external.ends_at)}</dd></>}
            </dl>
            {p.external.status === 'pending' && <p className="notice">{t('externalPending')}</p>}
            {p.external.pdf_url && (
              <a className="btn-ghost" href={p.external.pdf_url} target="_blank" rel="noreferrer"
                 style={{ marginTop: 12, display: 'inline-block' }}>{t('externalPdf')}</a>
            )}
          </div>
        </div>
      )}

      <div className="panel">
        <div className="panel-head"><span className="grow">{t('claims')}</span></div>
        <div className="panel-body flush">
          {(policy.data?.claims.length ?? 0) === 0
            ? <p className="empty">{t('noClaims')}</p>
            : <ClaimTable claims={policy.data!.claims.map(c => ({ ...c, product_name: p.product_name, policy: p.id }))} />}
        </div>
      </div>

      <div className="panel">
        <div className="panel-head"><span className="grow">{bond ? t('moveInEvidence') : t('evidence')}</span></div>
        <div className="panel-body stack" style={{ gap: 10 }}>
          {(policy.data?.evidence ?? []).map(e => (
            <div className="list-row" key={e.sha256}>
              <div className="grow">
                <div className="title">{e.filename}</div>
                <div className="sub">{[e.note, e.uploaded_by, day(e.uploaded_at)].filter(Boolean).join(' · ')}</div>
              </div>
              <button className="btn-ghost"
                onClick={() => download(`/policies/${encodeURIComponent(p.id)}/evidence/${e.sha256}`, e.filename)
                  .catch((err: Error) => message.error(err.message))}>
                <DownloadOutlined />
              </button>
            </div>
          ))}
          {party && (
            <>
              <label className="file-drop" onClick={() => input.current?.click()}>
                <PaperClipOutlined />
                <span className="name">{file ? file.name : t('attachFile')}</span>
                <input ref={input} type="file" accept="application/pdf,image/jpeg,image/png"
                  onChange={e => setFile(e.target.files?.[0] ?? null)} />
              </label>
              <div className="input-wrap">
                <input value={note} placeholder={t('noteWord')} onChange={e => setNote(e.target.value)} />
              </div>
              <button className="btn-small" disabled={!file || attach.isPending} onClick={() => attach.mutate()}>{t('save')}</button>
              <p className="field-hint">{t('fileTypes')}</p>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
